"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import WorkerProfileHeader from "@/app/components/WorkerProfileHeader";
import ChatPanel from "@/app/components/ChatPanel";
import TestingChatPanel from "@/app/components/TestingChatPanel";
import AgentChatPanel from "@/app/components/AgentChatPanel";
import ProfileDetails from "@/app/components/ProfileDetails";
import PricingBar from "@/app/components/PricingBar";
import { getLineSetupConfig } from "@/app/components/onboarding/lineSetupConfig";
import { useOrdersContext } from "@/context/OrdersContext";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { useIsHired } from "@/hooks/useIsHired";
import { servicesApi, serviceTagsApi, Service, ServiceTag, api } from "@/lib/api/index";
import { toAbsoluteImageUrl } from "@/lib/imageUtils";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { getProductIdForService } from "@/lib/getProductIdForService";
import { type Worker } from "@/app/components/workercard/WorkerCard";
import { useMobileDetails } from "@/hooks/useMobileDetails";
import { useTestChat } from "@/hooks/useTestChat";

interface WorkerData {
    id?: string;
    packageId?: string;
    name: string;
    stats?: string;
    rating?: string;
    likes?: number;
    completedPurchasesCount?: number;
    thumbnail?: string;
    bestFor: {
        title: string;
        subtitle: string;
        items: string[];
    };
    worksWith: string[];
    whatWorkerDoes: string[];
    builtByExpert: {
        name: string;
        title: string;
        image?: string;
        points: string[];
    };
    pricing: {
        price: string;
        tasksIncluded: string;
        savings: string;
    };
    packages?: Array<{
        id?: string;
        name: string;
        price: number;
        capacity: string;
        displayOrder?: number;
    }>;
    creator?: {
        id?: string;
        firstName?: string;
        lastName?: string;
        displayName?: string;
        avatar?: string;
    };
}

type OrderLike = {
    taskId?: string;
    task_id?: string;
    buyerId?: string;
    serviceId?: string;
    orderId?: string;
    id?: string;
    order?: { serviceId?: string };
    workspaceId?: string;
};

function transformServiceToWorkerData(service: Service, tags: ServiceTag[] = []): WorkerData {
    const firstPackage = service.packages?.[0];
    const price = firstPackage?.price ? `$${firstPackage.price}` : "$0";
    const capacity = firstPackage?.capacity || "0";
    const tasksIncluded = `${capacity}`;

    let bestForItems: string[] = [];
    let whatWorkerDoesItems: string[] = [];
    let expertPoints: string[] = [];
    let expertName = "ทีมผู้เชี่ยวชาญ"; // Expert Team
    let expertTitle = "ผู้ให้บริการ"; // Service Provider
    let expertImage = "";

    if (service.description.includes("|||")) {
        const parts = service.description.split("|||");

        if (parts[0]) {
            bestForItems = parts[0].split("\n").filter((line) => line.trim().length > 0);
        }
        if (parts[1]) {
            whatWorkerDoesItems = parts[1].split("\n").filter((line) => line.trim().length > 0);
        }
        if (parts[2]) {
            if (parts[2].includes("###EXPERT###")) {
                const [pointsSection, expertSection] = parts[2].split("###EXPERT###");
                expertPoints = pointsSection.split("\n").filter((line) => line.trim().length > 0);
                const expertMetadata = expertSection.trim();
                if (expertMetadata) {
                    const [name, title, image] = expertMetadata.split("|");
                    if (name) expertName = name;
                    if (title) expertTitle = title;
                    if (image) expertImage = image;
                }
            } else {
                expertPoints = parts[2].split("\n").filter((line) => line.trim().length > 0);
            }
        }
    } else {
        const descriptionLines = service.description
            .split("\n")
            .filter((line) => line.trim().length > 0);
        bestForItems = descriptionLines.slice(0, 3).length > 0 ? descriptionLines.slice(0, 3) : [];
        whatWorkerDoesItems = descriptionLines.length > 4 ? descriptionLines.slice(3, 7) : [];
        expertPoints = descriptionLines.length > 7 ? descriptionLines.slice(7) : [];
    }

    let worksWith: string[] = [];
    if (service.tagIds && service.tagIds.length > 0) {
        worksWith = service.tagIds.map((id) => {
            const foundTag = tags.find((t) => t.id === id);
            return foundTag ? foundTag.value : id;
        });
    } else if (tags.length > 0) {
        worksWith = tags.map((tag) => tag.value);
    }

    const creator = service.serviceCreator || service.creator;
    const packages =
        service.packages?.map((pkg) => ({
            id: pkg.id,
            name: pkg.name,
            price: pkg.price || 0,
            capacity: pkg.capacity || "0",
            displayOrder:
                typeof pkg.displayOrder === "number" ? pkg.displayOrder : 0,
        })) || [];

    return {
        id: service.id,
        packageId: firstPackage?.id,
        name: service.title,
        stats: firstPackage?.capacity || undefined,
        rating: "★ 0",
        likes: 0,
        completedPurchasesCount: service.completedPurchasesCount,
        thumbnail: toAbsoluteImageUrl(service.thumbnail),
        bestFor: {
            title: "เหมาะสำหรับ", // Best for
            subtitle: "เช็คความเหมาะสมอย่างรวดเร็ว", // Quick fit check
            items: bestForItems,
        },
        worksWith,
        whatWorkerDoes: whatWorkerDoesItems,
        builtByExpert: { name: expertName, title: expertTitle, image: expertImage, points: expertPoints },
        pricing: { price, tasksIncluded, savings: "0%" },
        packages,
        creator: creator
            ? {
                id: creator.id,
                firstName: creator.firstName,
                lastName: creator.lastName,
                displayName: creator.displayName,
                avatar:
                    service.serviceCreator?.profile?.avatar ||
                    service.serviceCreator?.avatar ||
                    creator.avatar,
            }
            : undefined,
    };
}

let cachedApiWorkers: Worker[] | null = null;

export function FirstResponder({ serviceId }: { serviceId?: string } = {}) {
    const params = useParams();
    const router = useRouter();
    const searchParams = useSearchParams();
    const id = (serviceId ?? (params?.id as string)) as string;
    const isEmbedded = searchParams?.get("embedded") === "true";
    const [workerData, setWorkerData] = useState<WorkerData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { showTestChat, setShowTestChat } = useTestChat();
    const [showLineSetupModal, setShowLineSetupModal] = useState(false);

    const { showMobileDetails, setShowMobileDetails } = useMobileDetails();
    const [overrideTaskId, setOverrideTaskId] = useState<string | null>(null);

    const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
    const [apiWorkers, setApiWorkers] = useState<Worker[]>(cachedApiWorkers || []);
    const [rightPanelWidth, setRightPanelWidth] = useState(500);
    const [isResizingRightPanel, setIsResizingRightPanel] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [agentInstalled, setAgentInstalled] = useState(false);
    const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);

    const handleContentScrollStateChange = useCallback((isScrolled: boolean) => {
        setIsHeaderScrolled(isScrolled);
    }, []);

    const [selectedWorkerSessionId, setSelectedWorkerSessionId] = useState<string | undefined>(undefined);

    useEffect(() => { setMounted(true); }, []);

    const [userId] = useState<string | undefined>(() => {
        if (typeof window === "undefined") return undefined;
        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const user = JSON.parse(userStr);
                return user.id || user.userId;
            }
        } catch (err) {
            console.error("Error parsing user data in initializer:", err);
        }
        return undefined;
    });

    const workspaceId = searchParams?.get("workspaceId");

    /** product_id สำหรับแชท / setup — ผูกกับ serviceId (route หรือ workerData) */
    const chatProductId = useMemo(
        () => getProductIdForService(workerData?.id ?? id),
        [workerData?.id, id],
    );

    const [fullOrderData, setFullOrderData] = useState<OrderLike | null>(null);
    const [orderDataResolved, setOrderDataResolved] = useState(false);

    const { orders, fetchOrders, loadingOrders } = useOrdersContext();
    const currentViewId = selectedWorker?.id || id;

    const { isHired: originalIsHired, hiredOrder } = useIsHired(currentViewId, userId);
    const [optimisticIsHired, setOptimisticIsHired] = useState(false);
    const isHired = originalIsHired || optimisticIsHired;

    useEffect(() => {
        setIsHeaderScrolled(false);
    }, [id, showTestChat, isHired]);
    const fetchFullOrder = useCallback(async () => {
        if (workspaceId && currentViewId) {
            try {
                const wsResponse = await api.orders.getByWorkspaceAndService(workspaceId, currentViewId) as OrderLike | null;
                if (!wsResponse) {
                    // 404 / no order for this workspace+service — resolve via hired order if any
                    if (hiredOrder?.id) {
                        try {
                            const response = await api.orders.getById(hiredOrder.id);
                            setFullOrderData(response as OrderLike);
                        } catch (err) {
                            console.error("Failed to fetch full order by id:", err);
                        }
                    }
                } else {
                    const resolvedOrderId = wsResponse?.orderId || wsResponse?.id || hiredOrder?.id;
                    if (resolvedOrderId && !wsResponse?.taskId && !wsResponse?.task_id) {
                        const fullResponse = await api.orders.getById(resolvedOrderId);
                        setFullOrderData(fullResponse as OrderLike);
                    } else {
                        setFullOrderData(wsResponse);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch full order by workspace/service:", error);
                if (hiredOrder?.id) {
                    try {
                        const response = await api.orders.getById(hiredOrder.id);
                        setFullOrderData(response as OrderLike);
                    } catch (err) {
                        console.error("Failed to fetch full order by id:", err);
                    }
                }
            }
            setOrderDataResolved(true);
            return;
        }
        if (hiredOrder?.id) {
            try {
                const response = await api.orders.getById(hiredOrder.id);
                setFullOrderData(response as OrderLike);
            } catch (error) {
                console.error("Failed to fetch full order:", error);
            }
            setOrderDataResolved(true);
            return;
        }
        setOrderDataResolved(true);
    }, [workspaceId, currentViewId, hiredOrder]);

    useEffect(() => {
        if (workspaceId) {
            fetchOrders("buyer", workspaceId);
        }
    }, [workspaceId, fetchOrders]);

    useEffect(() => {
        setOrderDataResolved(false);
        fetchFullOrder();
    }, [hiredOrder?.id, fetchFullOrder]);

    useEffect(() => {
        setOverrideTaskId(null);
        setShowMobileDetails(false);
    }, [currentViewId, setShowMobileDetails]);

    useEffect(() => {
        if (isHired) setShowMobileDetails(false);
    }, [isHired, setShowMobileDetails]);

    useEffect(() => {
        if (!currentViewId || typeof window === "undefined") return;
        if (loadingOrders && orders.length === 0) return;

        /** ต้องตรงกับ userId ใน JWT ที่ส่งไป Chat API — ไม่งั้น GET /v1/chats/history/... ได้ 403 "don't have access to this session" */
        let fromStorage: string | undefined;
        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const u = JSON.parse(userStr) as { id?: string; userId?: string };
                fromStorage = u.id || u.userId;
            }
        } catch {
            /* ignore */
        }
        const jwtUserId = getUserIdFromAuthToken();
        const effectiveUserId = jwtUserId || fromStorage || userId;

        let prefix = effectiveUserId || "visitor";
        if (isHired && workspaceId) prefix = workspaceId;

        const idToUse = isHired && hiredOrder?.id ? hiredOrder.id : currentViewId;
        setSelectedWorkerSessionId(`${prefix}-${idToUse}`);
    }, [currentViewId, userId, workspaceId, isHired, hiredOrder?.id, loadingOrders, orders.length]);

    useEffect(() => {
        const savedRightPanelWidth = localStorage.getItem("worker_right_panel_width");
        if (savedRightPanelWidth) setRightPanelWidth(parseInt(savedRightPanelWidth));
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isResizingRightPanel) {
                const newWidth = Math.max(400, Math.min(500, window.innerWidth - e.clientX));
                setRightPanelWidth(newWidth);
                localStorage.setItem("worker_right_panel_width", newWidth.toString());
            }
        };
        const handleMouseUp = () => {
            setIsResizingRightPanel(false);
            document.body.style.cursor = "default";
        };
        if (isResizingRightPanel) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "col-resize";
        }
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizingRightPanel]);

    const handleLineSetupSubmit = useCallback(async (formData: unknown) => {
        try {
            const taskId = overrideTaskId || fullOrderData?.taskId || hiredOrder?.taskId;
            const lineSetupConfig = getLineSetupConfig(chatProductId, taskId || null);
            const parsedConfig =
                typeof lineSetupConfig.config === "string"
                    ? JSON.parse(lineSetupConfig.config)
                    : lineSetupConfig.config;

            const headers: Record<string, string> = { "Content-Type": "application/json" };
            const token = localStorage.getItem("auth_token");
            if (token) {
                const cleanToken = token.trim().startsWith("Bearer ") ? token.trim().substring(7) : token.trim();
                headers["Authorization"] = `Bearer ${cleanToken}`;
            }

            const response = await fetch("/api/proxy", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    url: parsedConfig.endpoint_url,
                    method: parsedConfig.endpoint_method || "PUT",
                    headers,
                    data: formData,
                }),
            });

            if (!response.ok) throw new Error("Failed to save Line Setup configuration");
            setShowLineSetupModal(false);
        } catch (error) {
            console.error("[Line Setup] Error:", error);
            throw error;
        }
    }, [overrideTaskId, fullOrderData, hiredOrder, chatProductId]);

    // Sync line setup modal state to parent via postMessage
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (showLineSetupModal) {
            const taskId = overrideTaskId || fullOrderData?.taskId || hiredOrder?.taskId;
            const lineSetupConfig = getLineSetupConfig(chatProductId, taskId || null);
            window.parent.postMessage(
                {
                    type: "OPEN_DYNAMIC_SETUP_MODAL",
                    props: {
                        config: lineSetupConfig.config,
                        title: lineSetupConfig.step_title,
                        stepTitle: lineSetupConfig.step_title,
                        serviceId: workerData?.id,
                    },
                },
                "*"
            );
        } else {
            window.parent.postMessage({ type: "CLOSE_DYNAMIC_SETUP_MODAL" }, "*");
        }
    }, [showLineSetupModal, workerData, overrideTaskId, fullOrderData, hiredOrder, chatProductId]);

    // Handle incoming postMessages from parent
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            const data = event.data;
            if (!data) return;

            if (data.type === "MODAL_SUBMIT_REQUEST" && showLineSetupModal) {
                try {
                    await handleLineSetupSubmit(data.payload);
                    window.parent.postMessage({ type: "MODAL_SUBMIT_RESULT", success: true }, "*");
                } catch (err: unknown) {
                    console.error("Remote Submit Error:", err);
                    window.parent.postMessage(
                        { type: "MODAL_SUBMIT_RESULT", success: false, error: err instanceof Error ? err.message : "Unknown error" },
                        "*"
                    );
                }
            } else if (data.type === "MODAL_CLOSED_BY_USER") {
                if (data.modal === "dynamic_setup") setShowLineSetupModal(false);
            } else if (data.type === "RELOAD_CHAT_REQUEST") {
                setShowTestChat(false);
            } else if (data.type === "AERA_ORDER_CREATED") {
                setOptimisticIsHired(true);
                setShowMobileDetails(false);
                if (fetchOrders) fetchOrders();
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [showLineSetupModal, fetchOrders, setShowTestChat, handleLineSetupSubmit, setShowMobileDetails]);

    const fetchWorkerData = useCallback(async (silent = false) => {
        if (!id) {
            if (!silent) setError("Service ID is required");
            setLoading(false);
            return;
        }
        try {
            if (!silent) setLoading(true);
            setError(null);

            const currentId = selectedWorker?.id || id;
            const response = await servicesApi.getById(currentId);

            let service: Service;
            if (response && typeof response === "object") {
                if ("service" in response) {
                    service = (response as { service?: Service }).service as Service;
                } else if ("data" in response) {
                    service = (response as { data?: Service }).data as Service;
                } else {
                    service = response as Service;
                }
            } else {
                if (!silent) throw new Error("Invalid service response format");
                return;
            }

            if (!service || !service.id) {
                if (!silent) throw new Error("Service not found");
                return;
            }

            let tags: ServiceTag[] = [];
            if (service.tags && Array.isArray(service.tags) && service.tags.length > 0) {
                tags = service.tags;
            } else if (service.tagRelations && Array.isArray(service.tagRelations) && service.tagRelations.length > 0) {
                tags = service.tagRelations
                    .map((relation: unknown) => {
                        if (!relation || typeof relation !== "object") return null;
                        const r = relation as Record<string, unknown>;
                        if (r.value && r.id) return relation as ServiceTag;
                        if (r.tag && typeof r.tag === "object" && (r.tag as { value?: unknown }).value) return r.tag as ServiceTag;
                        if (r.serviceTag && typeof r.serviceTag === "object" && (r.serviceTag as { value?: unknown }).value) return r.serviceTag as ServiceTag;
                        return null;
                    })
                    .filter((t): t is ServiceTag => t !== null);
            } else if (service.tagIds && service.tagIds.length > 0) {
                try {
                    const tagPromises = service.tagIds.map(async (tagId) => {
                        try {
                            const res = await serviceTagsApi.getById(tagId);
                            if (res && typeof res === "object") {
                                if ("data" in res) return (res as { data: ServiceTag }).data;
                                if ("tag" in res) return (res as { tag: ServiceTag }).tag;
                                return res as ServiceTag;
                            }
                            return null;
                        } catch { return null; }
                    });
                    const resolvedTags = await Promise.all(tagPromises);
                    tags = resolvedTags.filter((t): t is ServiceTag => t !== null && typeof t === "object" && !!t.value);
                } catch { /* ignore */ }
            }

            setWorkerData(transformServiceToWorkerData(service, tags));
        } catch (err) {
            if (!silent) setError(err instanceof Error ? err.message : "Failed to load service data");
        } finally {
            setLoading(false);
        }
    }, [id, selectedWorker]);

    useEffect(() => {
        fetchWorkerData();
    }, [fetchWorkerData]);

    useEffect(() => {
        if (isEmbedded) return;
        async function fetchWorkers() {
            try {
                const servicesResponse = await api.services.list({ isActive: "true" });
                let services: Service[] = [];
                if (Array.isArray(servicesResponse)) {
                    services = servicesResponse;
                } else if (servicesResponse && typeof servicesResponse === "object" && "services" in servicesResponse) {
                    const data = (servicesResponse as { services?: Service[] }).services;
                    services = Array.isArray(data) ? data : [];
                }

                const transformedWorkers: Worker[] = services.reduce((acc, service, index) => {
                    if (!service.id) return acc;
                    const firstPackage = service.packages?.[0];
                    const creatorId = service.userId || service.creatorId || service.serviceCreatorId;
                    acc.push({
                        id: service.id || `api-${index}`,
                        name: service.title || "Unnamed Service",
                        role: service.description?.substring(0, 50) || "AI Worker",
                        archetype: "API Service",
                        rating: service.averageRating || 0,
                        hires: 0,
                        retainer: firstPackage?.price || 0,
                        wage: 0,
                        imageColor: "bg-indigo-100 text-indigo-600",
                        skills: [],
                        tone: "Professional and efficient",
                        description: service.description || "AI-powered service",
                        completedPurchasesCount: service.completedPurchasesCount || 0,
                        creatorId: creatorId,
                    });
                    return acc;
                }, [] as Worker[]);

                setApiWorkers(transformedWorkers);
                cachedApiWorkers = transformedWorkers;
            } catch {
                if (!cachedApiWorkers) setApiWorkers([]);
            }
        }
        fetchWorkers();
    }, [userId, isEmbedded]);

    useEffect(() => {
        if (apiWorkers.length > 0 && id) {
            const worker = apiWorkers.find((w) => w.id === id);
            if (worker) setSelectedWorker(worker);
        }
    }, [id, apiWorkers]);

    if (error) {
        return (
            <div className="min-h-screen bg-aera-white flex items-center justify-center">
                <div className="text-aera-red">{error || "Service not found"}</div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col md:flex-row relative overflow-hidden">
            <div className="flex-1 flex flex-col min-w-0 bg-aera-white">
                {/* Header */}
                <div
                    className={`sticky top-0 z-50 h-[81px] border-t border-aera-lightgray/80 shrink-0 bg-aera-white transition-shadow duration-200 ${
                        isHeaderScrolled
                            ? "shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08)]"
                            : "border-b-0 md:border-b"
                    }`}
                >
                    {loading || !workerData ? (
                        <div className="h-full w-full flex items-center px-4 gap-4 animate-pulse">
                            <div className="w-12 h-12 bg-aera-lightgray rounded-full" />
                            <div className="flex flex-col gap-2">
                                <div className="w-32 h-6 bg-aera-lightgray rounded" />
                                <div className="w-20 h-4 bg-aera-lightgray rounded" />
                            </div>
                        </div>
                    ) : (
                        <WorkerProfileHeader
                            name={workerData.name}
                            stats={workerData.stats}
                            rating={workerData.rating}
                            likes={workerData.likes}
                            completedPurchasesCount={workerData.completedPurchasesCount}
                            thumbnail={workerData.thumbnail}
                            creatorId={workerData.creator?.id}
                            serviceId={workerData.id}
                            onToggleSidebar={() => { }}
                            onOpenLineSetup={() => setShowLineSetupModal(true)}
                            isHired={isHired}
                            isOnboardingComplete={agentInstalled}
                            onOpenAgentChat={() => setShowTestChat(false)}
                            onOpenTestChat={() => setShowTestChat(true)}
                            activeTab={showTestChat ? "test" : "agent"}
                            showBackButton={true}
                            mobileOnlyBackButton={
                                !isEmbedded ||
                                (searchParams?.get("source") !== "marketplace" && searchParams?.get("view") !== "discover")
                            }
                            onBack={() => {
                                const source = searchParams?.get("source") || "";
                                const view = searchParams?.get("view") || "";
                                const workspaceIdParam = searchParams?.get("workspaceId") || "";

                                // Desired "back" targets for embedded view (mobile back button UX)
                                const targetUrl =
                                    source === "marketplace" && view === "discover"
                                        ? "/?view=discover"
                                        : source === "marketplace" && workspaceIdParam
                                            ? `/?workspaceId=${encodeURIComponent(workspaceIdParam)}`
                                        : source === "order" && workspaceIdParam
                                            ? `/?workspaceId=${encodeURIComponent(workspaceIdParam)}`
                                            : "";

                                if (isEmbedded) {
                                    window.parent.postMessage(
                                        { type: "CLOSE_SERVICE_VIEW", targetUrl },
                                        "*",
                                    );
                                } else {
                                    router.push(targetUrl || "/");
                                }
                            }}
                        />
                    )}
                </div>

                {/* Content Grid */}
                <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                    {/* Chat Panel */}
                    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                        {loading || !workerData ? (
                            <div className="h-full flex flex-col p-4 space-y-4 animate-pulse">
                                <div className="flex-1 bg-aera-white/50 rounded-xl flex items-center justify-center">
                                    <div className="text-aera-lightgray text-sm">Loading chat...</div>
                                </div>
                                <div className="h-12 w-full bg-aera-white rounded-xl" />
                            </div>
                        ) : (
                            (() => {
                                const orderTaskId = fullOrderData?.taskId ?? fullOrderData?.task_id;
                                const deployId = overrideTaskId || orderTaskId || hiredOrder?.taskId;
                                const effectiveDeployId = deployId || (!agentInstalled ? "temp-onboarding" : "");

                                if (!isHired) {
                                    return (
                                        <ChatPanel
                                            workerName={selectedWorker?.name || workerData.name}
                                            isHired={isHired}
                                            totalTasks={parseInt(workerData.pricing.tasksIncluded) || 10}
                                            productId={chatProductId}
                                            serviceId={currentViewId}
                                            workspaceId={workspaceId || undefined}
                                            userId={userId}
                                            sessionId={selectedWorkerSessionId}
                                            taskId={effectiveDeployId}
                                            onContentScrollStateChange={handleContentScrollStateChange}
                                        />
                                    );
                                }

                                return (
                                    <AgentChatPanel
                                        deployId={overrideTaskId || orderTaskId || hiredOrder?.taskId || "temp-onboarding"}
                                        workerName={workerData?.name || "AI Agent"}
                                        suggested_actions={true}
                                        isOnboarding={!agentInstalled}
                                        userId={fullOrderData?.buyerId || hiredOrder?.buyerId || userId}
                                        orderId={hiredOrder?.id}
                                        productId={chatProductId}
                                        deployIdResolved={!isHired || orderDataResolved}
                                        orderHasNoTaskId={!!(fullOrderData != null && !orderTaskId)}
                                        onInstalled={() => setAgentInstalled(true)}
                                        layoutResizeKey={rightPanelWidth}
                                        onContentScrollStateChange={handleContentScrollStateChange}
                                    />
                                );
                            })()
                        )}
                    </div>

                    {/* Right Panel Resize Handle */}
                    <div
                        className={`${agentInstalled && !showTestChat ? "hidden" : "hidden xl:block"} w-px relative group cursor-col-resize hover:bg-aera-lightgray transition-colors z-10 ${isResizingRightPanel ? "bg-aera-amberlight" : "bg-transparent"}`}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            setIsResizingRightPanel(true);
                        }}
                    >
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-8 bg-aera-white border border-aera-lightgray/80 rounded-full flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-1 h-1 bg-aera-gray rounded-full" />
                            <div className="w-1 h-1 bg-aera-gray rounded-full" />
                            <div className="w-1 h-1 bg-aera-gray rounded-full" />
                        </div>
                    </div>

                    {/* Mobile Overlay */}
                    {showMobileDetails && (
                        <div
                            className="absolute inset-0 z-40 bg-aera-charcoal/50 backdrop-blur-sm xl:hidden cursor-pointer"
                            onClick={() => setShowMobileDetails(false)}
                        />
                    )}

                    {/* Right Panel */}
                    {workerData && (
                        <div
                            style={{
                                width: `${!isHired || showTestChat
                                    ? (mounted && window.innerWidth < 1024 ? window.innerWidth : rightPanelWidth)
                                    : 0}px`,
                            }}
                            className={`
                                fixed inset-y-0 right-0 z-50 md:mt-auto xl:mt-0 bg-aera-white xl:bg-transparent xl:border-t-0 border-t border-l border-aera-lightgray shadow-2xl transition-width duration-300 ease-in-out xl:relative xl:translate-x-0 xl:shadow-none xl:z-0
                                w-full max-w-full xl:max-w-none xl:w-auto h-dvh md:h-[calc(100dvh-56px)] xl:h-auto
                                ${showMobileDetails || (showTestChat && typeof window !== "undefined" && window.innerWidth < 1024) ? "translate-x-0" : "translate-x-[110%]"}
                            `}
                        >
                            {!isHired ? (
                                <div className="h-full flex flex-col">
                                    <div className="flex xl:hidden items-center justify-between p-4 border-b border-aera-lightgray">
                                        <h3 className="font-bold text-aera-charcoal">Details</h3>
                                        <AeraButton variant="ghost" size="xs" textColor="gray" onClick={() => setShowMobileDetails(false)} className="p-1!">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </AeraButton>
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                        <ProfileDetails
                                            bestFor={workerData.bestFor}
                                            worksWith={workerData.worksWith}
                                            whatWorkerDoes={workerData.whatWorkerDoes}
                                            builtByExpert={workerData.builtByExpert}
                                            workerData={workerData}
                                        />
                                    </div>
                                    <PricingBar
                                            key={workerData.id}
                                            price={workerData.pricing.price}
                                            tasksIncluded={workerData.pricing.tasksIncluded}
                                            savings={workerData.pricing.savings}
                                            serviceId={workerData.id}
                                            packageId={workerData.packageId}
                                            title={workerData.name}
                                            packages={workerData.packages}
                                            thumbnail={workerData.thumbnail}
                                            expertName={workerData.builtByExpert.name}
                                            expertTitle={workerData.builtByExpert.title}
                                            workspaceId={workspaceId || undefined}
                                            onHireRequest={() => { }}
                                        />
                                </div>
                            ) : agentInstalled ? (
                                showTestChat ? (
                                    <div className="relative h-full flex flex-col">
                                        <div className="flex xl:hidden items-center justify-between p-4 border-b border-aera-lightgray bg-aera-white">
                                            <h3 className="font-bold text-aera-charcoal">Test Chat</h3>
                                            <AeraButton variant="ghost" size="xs" textColor="gray" onClick={() => setShowTestChat(false)} className="p-2! rounded-full">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </AeraButton>
                                        </div>
                                        <div className="flex-1 overflow-hidden">
                                            <TestingChatPanel
                                                workerName={selectedWorker?.name || workerData.name}
                                                taskId={overrideTaskId || fullOrderData?.taskId || hiredOrder?.taskId || ""}
                                                onContentScrollStateChange={handleContentScrollStateChange}
                                            />
                                        </div>
                                    </div>
                                ) : null
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
